import { useEffect, useState } from 'react';
import { Confirm, toast, type Destructive } from '@drbell/ui';
import { api, ApiError, type OrgSummary } from '../api.ts';

/**
 * Sending dose events to the home's own systems — RX-ORG-13.
 *
 * The API has had webhooks since the organisation work began, and nothing
 * could reach them: "webhooks are API-only" on the reachability list, which
 * for a care home means "not a feature". Managers only, like the routes.
 *
 * The secret is shown once, when an endpoint is added or its key is rotated,
 * and never again — the server does not return it after that. The screen
 * says so next to the secret, because a receiver that did not store it has to
 * rotate, and finding that out later is an outage.
 */
interface Endpoint {
  id: string;
  url: string;
  events: string[];
  enabled: boolean;
  lastSuccessAt: number | null;
  lastFailureAt: number | null;
  failureCount: number;
  disabledByFailures: boolean;
}

interface ListResponse {
  webhooks: Endpoint[];
  availableEvents: string[];
  failureLimit: number;
}

interface Revealed {
  url: string;
  secret: string;
}

/** What each event means, in the words a care-home IT person would use. */
const EVENT_LABEL: Record<string, string> = {
  'dose.taken': 'A dose was given or taken',
  'dose.skipped': 'A dose was deliberately skipped',
  'dose.late': 'A dose is running late',
  'dose.missed': 'A dose was missed',
  'refill.low': 'A medicine is running low',
};

const reason = (e: unknown) => (e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));

const when = (ms: number | null) =>
  ms ? new Date(ms).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : 'never';

export function Webhooks({ org }: { org: OrgSummary }) {
  const [data, setData] = useState<ListResponse | null>(null);
  const [url, setUrl] = useState('https://');
  const [events, setEvents] = useState<string[]>(['dose.missed']);
  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Destructive | null>(null);

  const load = async () => {
    try {
      setData(await api.get<ListResponse>(`/api/orgs/${org.id}/webhooks`));
    } catch (e) {
      setError(reason(e));
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id]);

  const add = async () => {
    setBusy('add');
    setError(null);
    try {
      const created = await api.post<{ url: string; secret: string }>(`/api/orgs/${org.id}/webhooks`, {
        url: url.trim(),
        events,
      });
      setRevealed({ url: created.url, secret: created.secret });
      setUrl('https://');
      await load();
    } catch (e) {
      setError(reason(e));
    } finally {
      setBusy(null);
    }
  };

  const update = async (endpoint: Endpoint, change: { enabled?: boolean; events?: string[]; rotateSecret?: boolean }) => {
    setBusy(endpoint.id);
    try {
      const result = await api.put<{ secret?: string }>(`/api/orgs/${org.id}/webhooks/${endpoint.id}`, change);
      if (result.secret) setRevealed({ url: endpoint.url, secret: result.secret });
      else toast.success(change.enabled === false ? 'Switched off' : change.enabled ? 'Switched on' : 'Saved');
      await load();
    } catch (e) {
      toast.error('Could not save', reason(e));
    } finally {
      setBusy(null);
    }
  };

  const rotate = (endpoint: Endpoint) =>
    setConfirming({
      title: 'Issue a new signing key?',
      body: `The receiver at ${endpoint.url} stops accepting our deliveries until it has the new key.`,
      reassurance: 'The address and the events stay as they are.',
      confirmLabel: 'Issue new key',
      onConfirm: async () => {
        setConfirming(null);
        await update(endpoint, { rotateSecret: true });
      },
    });

  const remove = (endpoint: Endpoint) =>
    setConfirming({
      title: 'Stop sending to this address?',
      body: `Nothing more will be sent to ${endpoint.url}. Deliveries already on their way may still arrive.`,
      reassurance: 'Residents’ records are not affected.',
      confirmLabel: 'Remove',
      onConfirm: async () => {
        setConfirming(null);
        try {
          await api.del(`/api/orgs/${org.id}/webhooks/${endpoint.id}`);
          toast.success('Removed');
          await load();
        } catch (e) {
          toast.error('Could not remove', reason(e));
        }
      },
    });

  const toggle = (list: string[], event: string) =>
    list.includes(event) ? list.filter((e) => e !== event) : [...list, event];

  return (
    <section className="space-y-5">
      {confirming && <Confirm {...confirming} onCancel={() => setConfirming(null)} />}
      <div>
        <h2 className="text-lg font-semibold">Send events to your own systems</h2>
        <p className="text-sm text-(--muted)">
          For a rostering system or health record that wants to know, as it happens, when a
          resident&rsquo;s dose was given, is late or was missed. We send a signed POST to the address
          you give. It has to be https, and it cannot be a private or internal address.
        </p>
      </div>

      {revealed && (
        <div className="space-y-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
          <p className="text-sm font-medium">Signing key for {revealed.url}</p>
          <code className="block overflow-x-auto rounded-lg bg-(--panel) p-2 font-mono text-xs select-all">
            {revealed.secret}
          </code>
          <p className="text-sm text-(--muted)">
            Copy it into the receiving system now. It is not shown again — if it is lost, issue a new
            one. Each delivery carries <code>x-drbell-signature: t=&lt;seconds&gt;,v1=&lt;hex&gt;</code>, an
            HMAC-SHA256 of <code>&lt;t&gt;.&lt;body&gt;</code> with this key.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => void navigator.clipboard?.writeText(revealed.secret).then(() => toast.success('Copied'))}
              className="min-h-11 rounded-lg border border-(--line) px-3 text-sm"
            >
              Copy
            </button>
            <button onClick={() => setRevealed(null)} className="min-h-11 rounded-lg px-3 text-sm text-(--muted)">
              I have stored it
            </button>
          </div>
        </div>
      )}

      {!data && !error && <p className="text-sm text-(--muted)">Loading…</p>}

      {data && data.webhooks.length > 0 && (
        <ul className="space-y-3">
          {data.webhooks.map((w) => (
            <li key={w.id} className="space-y-3 rounded-xl border border-(--line) p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-mono text-sm">{w.url}</p>
                  <p className={`text-xs ${w.enabled ? 'text-(--muted)' : 'text-red-600'}`}>
                    {w.enabled
                      ? `On · last delivered ${when(w.lastSuccessAt)}`
                      : w.disabledByFailures
                        ? `Switched off after ${w.failureCount} failed deliveries in a row. Fix the receiver, then switch it back on.`
                        : 'Switched off'}
                    {w.enabled && w.failureCount > 0 &&
                      ` · ${w.failureCount} of ${data.failureLimit} failures in a row (last ${when(w.lastFailureAt)})`}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    disabled={busy !== null}
                    onClick={() => void update(w, { enabled: !w.enabled })}
                    className="min-h-11 rounded-lg border border-(--line) px-3 text-sm disabled:opacity-40"
                  >
                    {w.enabled ? 'Switch off' : 'Switch on'}
                  </button>
                  <button
                    disabled={busy !== null}
                    onClick={() => rotate(w)}
                    className="min-h-11 rounded-lg border border-(--line) px-3 text-sm disabled:opacity-40"
                  >
                    New key
                  </button>
                  <button
                    disabled={busy !== null}
                    onClick={() => remove(w)}
                    className="min-h-11 rounded-lg border border-red-500 px-3 text-sm text-red-600 disabled:opacity-40"
                  >
                    Remove
                  </button>
                </div>
              </div>
              <fieldset className="flex flex-wrap gap-x-4 gap-y-2">
                <legend className="sr-only">Events sent to {w.url}</legend>
                {data.availableEvents.map((event) => (
                  <label key={event} className="flex min-h-11 items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      disabled={busy !== null}
                      checked={w.events.includes(event)}
                      onChange={() => {
                        const next = toggle(w.events, event);
                        // At least one: an endpoint subscribed to nothing is
                        // one that should be removed, and the API refuses it.
                        if (next.length > 0) void update(w, { events: next });
                      }}
                    />
                    {EVENT_LABEL[event] ?? event}
                  </label>
                ))}
              </fieldset>
            </li>
          ))}
        </ul>
      )}

      {data && (
        <div className="space-y-3 rounded-xl border border-(--line) p-4">
          <label className="block">
            <span className="text-sm font-medium">Add an address</span>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://ehr.example.org/drbell"
              className="mt-2 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3 font-mono text-sm"
            />
          </label>
          <fieldset className="flex flex-wrap gap-x-4 gap-y-2">
            <legend className="mb-1 text-sm font-medium">Send</legend>
            {data.availableEvents.map((event) => (
              <label key={event} className="flex min-h-11 items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={events.includes(event)}
                  onChange={() => setEvents((list) => toggle(list, event))}
                />
                {EVENT_LABEL[event] ?? event}
              </label>
            ))}
          </fieldset>
          <button
            disabled={busy !== null || events.length === 0 || !/^https:\/\/.+/.test(url.trim())}
            onClick={() => void add()}
            className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy === 'add' ? 'Adding…' : 'Add address'}
          </button>
        </div>
      )}

      {error && <p className="rounded-lg bg-red-500/10 p-3 text-sm text-red-600">{error}</p>}
    </section>
  );
}
